/**
 * Dashboard Component
 * 
 * 사용자 플랜 정보, 사용량, 기능 현황을 상세하게 보여주는 대시보드 페이지
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { getHistory } from '../api';
import type { QuotaInfo } from '../api';
import StatisticsModal from './StatisticsModal';
import TestcaseAnalytics from './TestcaseAnalytics';
import CriteriaSettingsModal from './CriteriaSettingsModal';

interface DashboardProps {
  onBack: () => void;
  onUpgrade: () => void;
}

export default function Dashboard({ onBack, onUpgrade }: DashboardProps) {
  const { quota, isAuthenticated } = useAuth();
  const { t, lang } = useLanguage();
  const [historyUsage, setHistoryUsage] = useState<{ used: number; limit: number } | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showTestcaseAnalytics, setShowTestcaseAnalytics] = useState(false);
  const [showCriteriaSettings, setShowCriteriaSettings] = useState(false);

  // 히스토리 사용량 로드
  useEffect(() => {
    if (isAuthenticated && quota && quota.limits.history_limit > 0) {
      setIsLoadingHistory(true);
      getHistory()
        .then((data) => {
          setHistoryUsage({ used: data.total, limit: data.limit });
        })
        .catch(() => {
          // 에러 시 기본값
          setHistoryUsage({ used: 0, limit: quota.limits.history_limit });
        })
        .finally(() => {
          setIsLoadingHistory(false);
        });
    }
  }, [isAuthenticated, quota]);
  
  if (!isAuthenticated || !quota) {
    return (
      <div className="neo-card-static bg-[var(--neo-blue)] p-6">
        <p className="text-black font-bold">{t.auth.loginRequired}</p>
        <button onClick={onBack} className="neo-btn bg-white text-black mt-4 px-4 py-2">
          {t.dashboard.backToHome}
        </button>
      </div>
    );
  }
  
  const { monthly_evaluations, limits, team } = quota as QuotaInfo;
  const { used, limit, unlimited } = monthly_evaluations;
  
  // 사용률 계산
  const usagePercent = unlimited ? 0 : Math.min((used / limit) * 100, 100);
  const isNearLimit = !unlimited && used >= limit * 0.8;
  const isAtLimit = !unlimited && used >= limit;
  
  // 팀 사용 여부
  const isTeamPlan = team !== null && team !== undefined;
  
  // 플랜 색상
  const planColors: Record<string, { bg: string; accent: string }> = {
    free: { bg: 'bg-[var(--neo-blue)]', accent: 'bg-white' },
    pro: { bg: 'bg-[var(--neo-mint)]', accent: 'bg-black' },
    max: { bg: 'bg-[var(--neo-orange)]', accent: 'bg-white' },
    team: { bg: 'bg-[var(--neo-yellow)]', accent: 'bg-black' },
  };
  
  const colors = planColors[quota.plan] || planColors.free;
  
  // 역할 텍스트
  const getRoleText = () => {
    if (!isTeamPlan) return '';
    const role = team?.my_role;
    if (role === 'owner') return lang === 'ko' ? '오너(리드)' : 'Owner(Lead)';
    if (role === 'manager') return lang === 'ko' ? '매니저' : 'Manager';
    return lang === 'ko' ? '멤버' : 'Member';
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="neo-btn bg-white dark:bg-[var(--neo-blue)] text-black text-sm px-3 py-1.5 flex items-center gap-1"
        >
          {t.dashboard.backToHome}
        </button>
        <h1 className="text-lg font-black text-black dark:text-white uppercase tracking-wide">
          {t.dashboard.title}
        </h1>
      </div>
      
      {/* 현재 플랜 카드 */}
      <div className={`neo-card-static ${colors.bg} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-xs font-bold text-black/60 dark:text-white/70 uppercase">
              {t.dashboard.currentPlan}
            </span>
            <h2 className="text-3xl font-black text-black dark:text-white uppercase tracking-tight mt-1">
              {quota.plan.toUpperCase()}
              {isTeamPlan && (
                <span className="text-lg ml-2">
                  👥 {lang === 'ko' ? '팀' : 'Team'}
                </span>
              )}
            </h2>
          </div>
          {quota.plan !== 'max' && quota.plan !== 'team' && !isTeamPlan && (
            <button
              onClick={onUpgrade}
              className="neo-btn bg-black text-[var(--neo-lime)] px-4 py-2 text-sm"
            >
              {t.dashboard.upgradeCta}
            </button>
          )}
        </div>
        
        {/* Team 플랜: 팀 정보 + 월간 평가를 반반(한 줄)로 */}
        {isTeamPlan && team ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/50 dark:bg-white/10 border-2 border-black dark:border-white/30 p-3 min-w-0">
              <h3 className="text-xs font-bold text-black/60 dark:text-white/70 uppercase mb-2">
                {t.dashboard.teamInfo}
              </h3>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="min-w-0">
                  <span className="text-black/60 dark:text-white/60">{t.dashboard.teamRole}:</span>
                  <span className="font-bold text-black dark:text-white ml-1">{getRoleText()}</span>
                </div>
                <div className="min-w-0">
                  <span className="text-black/60 dark:text-white/60">{lang === 'ko' ? '오너 플랜' : 'Owner Plan'}:</span>
                  <span className="font-bold text-black dark:text-white ml-1 uppercase">{team.owner_plan}</span>
                </div>
              </div>
            </div>

            <div className="bg-white/50 dark:bg-white/10 border-2 border-black dark:border-white/30 p-4 min-w-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-black/70 dark:text-white/80 uppercase">
                  {t.dashboard.monthlyEvaluations}
                </span>
                <span className={`text-xl font-black text-black dark:text-white ${isAtLimit ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {unlimited ? (
                    <span className="bg-black dark:bg-[var(--neo-mint)] text-[var(--neo-lime)] dark:text-black px-3 py-1 text-sm">
                      ∞ {t.dashboard.unlimited}
                    </span>
                  ) : (
                    <>
                      <span className="text-2xl">{used}</span>
                      <span className="text-lg text-black/50 dark:text-white/50"> / {limit}</span>
                    </>
                  )}
                </span>
              </div>
              
              {/* 프로그레스 바 */}
              {!unlimited && (
                <div className="h-5 bg-white dark:bg-white/20 border-2 border-black dark:border-white/30 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-[var(--neo-orange)]' : 'bg-black dark:bg-[var(--neo-mint)]'
                    }`}
                    style={{ width: `${usagePercent}%` }}
                  />
                </div>
              )}
              
              {!unlimited && (
                <div className="flex justify-between mt-2 text-xs font-bold text-black/60 dark:text-white/60">
                  <span>{used} {t.dashboard.used}</span>
                  <span>{limit - used} {t.dashboard.remaining}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* 그 외 플랜: 기존 단일 사용량 섹션 */
          <div className="bg-white/50 dark:bg-white/10 border-2 border-black dark:border-white/30 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-black/70 dark:text-white/80 uppercase">
              {t.dashboard.monthlyEvaluations}
            </span>
            <span className={`text-xl font-black text-black dark:text-white ${isAtLimit ? 'text-red-600 dark:text-red-400' : ''}`}>
              {unlimited ? (
                <span className="bg-black dark:bg-[var(--neo-mint)] text-[var(--neo-lime)] dark:text-black px-3 py-1 text-sm">
                  ∞ {t.dashboard.unlimited}
                </span>
              ) : (
                <>
                  <span className="text-2xl">{used}</span>
                  <span className="text-lg text-black/50 dark:text-white/50"> / {limit}</span>
                </>
              )}
            </span>
          </div>
          
          {/* 프로그레스 바 */}
          {!unlimited && (
            <div className="h-5 bg-white dark:bg-white/20 border-2 border-black dark:border-white/30 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-[var(--neo-orange)]' : 'bg-black dark:bg-[var(--neo-mint)]'
                }`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          )}
          
          {!unlimited && (
            <div className="flex justify-between mt-2 text-xs font-bold text-black/60 dark:text-white/60">
              <span>{used} {t.dashboard.used}</span>
              <span>{limit - used} {t.dashboard.remaining}</span>
            </div>
          )}
          </div>
        )}
      </div>
      
      {/* 플랜 기능 카드 */}
      <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-6">
        <h3 className="text-sm font-black text-black dark:text-white uppercase mb-4 flex items-center gap-2">
          <span className="w-2 h-2 bg-[var(--neo-mint)]"></span>
          {t.dashboard.planFeatures}
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* 테스트케이스 제한 */}
          <FeatureItem
            icon="📋"
            label={t.dashboard.testCasesPerEval}
            value={`${limits.max_test_cases}${t.dashboard.items}`}
          />
          
          {/* Planning 글자 수 */}
          <FeatureItem
            icon="📝"
            label={t.dashboard.planningChars}
            value={`${limits.max_planning_chars.toLocaleString()}${t.dashboard.chars}`}
          />
          
          {/* 히스토리 */}
          <FeatureItem
            icon="📚"
            label={t.dashboard.historyStorage}
            value={limits.history_limit > 0 
              ? `${limits.history_limit}${lang === 'ko' ? '건' : ' items'}`
              : t.dashboard.notAvailable}
            available={limits.history_limit > 0}
            usage={historyUsage ? { used: historyUsage.used, limit: historyUsage.limit } : undefined}
            isLoading={isLoadingHistory}
            lang={lang}
          />
          
          {/* PDF 다운로드 */}
          <FeatureItem
            icon="📄"
            label={t.dashboard.pdfDownload}
            value={limits.pdf_download ? t.dashboard.available : t.dashboard.notAvailable}
            available={limits.pdf_download}
          />
          
          {/* API 액세스 */}
          <FeatureItem
            icon="🔌"
            label={t.dashboard.apiAccess}
            value={limits.api_access ? t.dashboard.available : t.dashboard.notAvailable}
            available={limits.api_access}
          />
          
          {/* 팀 기능 - Business 플랜에서만 가능 */}
          <FeatureItem
            icon="👥"
            label={t.dashboard.teamFeature}
            value={quota.plan === 'team' ? t.dashboard.available : t.dashboard.notAvailable}
            available={quota.plan === 'team'}
          />
        </div>
      </div>
      
      {/* 통계 섹션 - Pro 이상 플랜만 */}
      {limits.history_limit > 0 && (
        <div className="neo-card-static bg-gradient-to-br from-[#1e293b] to-[#0f172a] dark:from-[#1e2a3a] dark:to-[#0f1a2a] p-6 h-[100px]">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-status-go/20 to-status-warning/20 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">📊</span>
              </div>
              <div>
                <h3 className="text-sm font-black text-white uppercase">
                  {t.statistics?.title || (lang === 'ko' ? '평가 통계' : 'Evaluation Statistics')}
                </h3>
                <p className="text-xs text-white/60 mt-0.5 line-clamp-1">
                  {t.statistics?.description || (lang === 'ko' ? '일별 평가 추이와 상태별 분포를 확인하세요' : 'View daily trends and status distribution')}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowStatistics(true)}
              className="neo-btn bg-status-go text-white w-[140px] py-2 text-sm flex items-center justify-center gap-2 flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {lang === 'ko' ? '자세히 보기' : 'View Details'}
            </button>
          </div>
        </div>
      )}
      
      {/* 통계 모달 */}
      <StatisticsModal
        isOpen={showStatistics}
        onClose={() => setShowStatistics(false)}
      />
      
      {/* 테스트케이스 분석 섹션 */}
      {limits.history_limit > 0 && (
        <div className="neo-card-static bg-gradient-to-br from-[#1e2a3a] to-[#0f1a2a] dark:from-[#1e2a3a] dark:to-[#0f1a2a] p-6 h-[100px]">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🔬</span>
              </div>
              <div>
                <h3 className="text-sm font-black text-white uppercase flex items-center gap-2">
                  {lang === 'ko' ? '테스트케이스 분석' : 'Test Case Analytics'}
                  <span className="text-[10px] font-medium bg-gradient-to-r from-purple-500 to-cyan-500 text-white px-2 py-0.5 rounded">
                    NEW
                  </span>
                </h3>
                <p className="text-xs text-white/60 mt-0.5 line-clamp-1">
                  {lang === 'ko' ? '서비스 취약 영역을 파악하세요' : 'Identify service vulnerabilities'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowTestcaseAnalytics(true)}
              className="neo-btn bg-gradient-to-r from-purple-500 to-cyan-500 text-white w-[140px] py-2 text-sm flex items-center justify-center gap-2 flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {lang === 'ko' ? '분석 보기' : 'View Analytics'}
            </button>
          </div>
        </div>
      )}
      
      {/* 테스트케이스 분석 모달 */}
      <TestcaseAnalytics
        isOpen={showTestcaseAnalytics}
        onClose={() => setShowTestcaseAnalytics(false)}
      />
      
      {/* 릴리즈 판정 기준 설정 섹션 - Max/Team 플랜 전용 */}
      {(quota.plan === 'max' || quota.plan === 'team') && (
        <div className="neo-card-static bg-gradient-to-br from-[#2d1b4e] to-[#1a0f30] dark:from-[#2d1b4e] dark:to-[#1a0f30] p-6 h-[100px]">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-pink-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚙️</span>
              </div>
              <div>
                <h3 className="text-sm font-black text-white uppercase flex items-center gap-2">
                  {t.criteria?.title || (lang === 'ko' ? '릴리즈 판정 기준 설정' : 'Release Criteria Settings')}
                  <span className="text-[10px] font-medium bg-gradient-to-r from-orange-500 to-pink-500 text-white px-2 py-0.5 rounded">
                    MAX
                  </span>
                </h3>
                <p className="text-xs text-white/60 mt-0.5 line-clamp-1">
                  {t.criteria?.description || (lang === 'ko' ? 'Go/No-Go 기준을 커스터마이즈하세요' : 'Customize your Go/No-Go criteria')}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowCriteriaSettings(true)}
              className="neo-btn bg-gradient-to-r from-orange-500 to-pink-500 text-white w-[140px] py-2 text-sm flex items-center justify-center gap-2 flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {lang === 'ko' ? '설정하기' : 'Configure'}
            </button>
          </div>
        </div>
      )}
      
      {/* 릴리즈 판정 기준 모달 */}
      <CriteriaSettingsModal
        isOpen={showCriteriaSettings}
        onClose={() => setShowCriteriaSettings(false)}
      />
      
      {/* 팀 대시보드 섹션 - Team 플랜 전용 */}
      {quota.plan === 'team' && limits.team_dashboard && (
        <>
          <div className="neo-card-static bg-gradient-to-br from-[#1a2634] to-[#0d1520] dark:from-[#1a2634] dark:to-[#0d1520] p-6 h-[100px]">
            <div className="flex items-center justify-between h-full">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--neo-yellow)]/20 to-[var(--neo-orange)]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">👥</span>
                </div>
                <div>
                  <h3 className="text-sm font-black text-white uppercase flex items-center gap-2">
                    {t.dashboard.teamDashboardTitle || (lang === 'ko' ? '팀 대시보드' : 'Team Dashboard')}
                    <span className="text-[10px] font-medium bg-[var(--neo-yellow)] text-black px-2 py-0.5 rounded">
                      TEAM
                    </span>
                  </h3>
                  <p className="text-xs text-white/60 mt-0.5 line-clamp-1">
                    {lang === 'ko' ? '팀 전체 릴리즈 품질 현황' : "Team release quality overview"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowStatistics(true)}
                className="neo-btn bg-[var(--neo-yellow)] text-black w-[140px] py-2 text-sm flex items-center justify-center gap-2 flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {lang === 'ko' ? '팀 대시보드' : 'Team Dashboard'}
              </button>
            </div>
          </div>
          
          {/* 팀 대시보드 미리보기 카드 */}
          <div className="neo-card-static bg-gradient-to-br from-[#1a2634] to-[#0d1520] dark:from-[#1a2634] dark:to-[#0d1520] p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                <div className="flex items-center gap-2 text-xs text-white/60 mb-1">
                  <span>📈</span>
                  <span>{t.dashboard.teamTrendAnalysis || (lang === 'ko' ? '팀 트렌드 분석' : 'Team Trend Analysis')}</span>
                </div>
                <p className="text-sm font-bold text-white">
                  {lang === 'ko' ? '팀 전체 평가 추이 확인' : 'View team-wide evaluation trends'}
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                <div className="flex items-center gap-2 text-xs text-white/60 mb-1">
                  <span>👤</span>
                  <span>{t.dashboard.teamMemberStats || (lang === 'ko' ? '팀원별 통계' : 'Member Statistics')}</span>
                </div>
                <p className="text-sm font-bold text-white">
                  {lang === 'ko' ? '팀원별 평가 현황 비교' : 'Compare member evaluations'}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* 업그레이드 프롬프트 (Pro/Business가 아닐 때) */}
      {quota.plan === 'free' && (
        <div className="neo-card-static bg-[var(--neo-pink)] dark:bg-[#2d3a4f] p-6 text-center">
          <p className="text-black dark:text-white font-bold text-lg mb-3">
            {t.dashboard.upgradePrompt}
          </p>
          <button
            onClick={onUpgrade}
            className="neo-btn bg-black text-[var(--neo-lime)] px-6 py-2"
          >
            {t.dashboard.upgradeCta} →
          </button>
        </div>
      )}
    </div>
  );
}

// 개별 기능 아이템 컴포넌트
function FeatureItem({ 
  icon, 
  label, 
  value, 
  available = true,
  usage,
  isLoading = false,
  lang = 'ko'
}: { 
  icon: string; 
  label: string; 
  value: string; 
  available?: boolean;
  usage?: { used: number; limit: number };
  isLoading?: boolean;
  lang?: string;
}) {
  const usagePercent = usage ? Math.min((usage.used / usage.limit) * 100, 100) : 0;
  const isNearLimit = usage && usage.used >= usage.limit * 0.8;
  
  return (
    <div className={`flex items-center gap-3 p-3 border-2 relative ${
      available 
        ? 'bg-white dark:bg-black/20 border-black dark:border-[var(--neo-mint)]' 
        : 'bg-gray-200 dark:bg-black/50 border-gray-300 dark:border-gray-600'
    }`}>
      {/* 비활성화 오버레이 */}
      {!available && (
        <div className="absolute inset-0 bg-gray-400/10 dark:bg-black/20 pointer-events-none" />
      )}
      <span className={`text-xl ${!available ? 'grayscale opacity-40' : ''}`}>{icon}</span>
      <div className="flex-1">
        <span className={`text-xs font-bold block ${
          available ? 'text-black/60 dark:text-white/60' : 'text-black/30 dark:text-white/30'
        }`}>
          {label}
        </span>
        {isLoading ? (
          // 로딩 중
          <div className="mt-1">
            <div className="h-4 bg-black/10 dark:bg-white/10 animate-pulse rounded"></div>
          </div>
        ) : usage ? (
          // 사용량이 있는 경우 프로그레스와 함께 표시
          <div className="mt-1">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={`font-black ${isNearLimit ? 'text-[var(--neo-orange)]' : 'text-black dark:text-white'}`}>
                {usage.used} / {usage.limit}
              </span>
              <span className="text-black/40 dark:text-white/40">
                {lang === 'ko' ? '사용 중' : 'used'}
              </span>
            </div>
            <div className="h-1.5 bg-black/10 dark:bg-white/10 overflow-hidden rounded-full">
              <div 
                className={`h-full transition-all ${isNearLimit ? 'bg-[var(--neo-orange)]' : 'bg-[var(--neo-mint)]'}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          </div>
        ) : (
          <span className={`text-sm font-black ${
            available ? 'text-black dark:text-white' : 'text-black/30 dark:text-white/30 line-through'
          }`}>
            {value}
          </span>
        )}
      </div>
      {available && !usage ? (
        <span className="w-2 h-2 bg-[var(--neo-mint)] rounded-full"></span>
      ) : !available ? (
        <span className="text-xs font-bold text-red-400 dark:text-red-500">✕</span>
      ) : null}
    </div>
  );
}

